/**
 * 어시스턴트가 제안한 캔버스를 실제 캔버스에 넣을 수 있는 형태로 다듬는다.
 *
 * 제안은 서비스 쪽 validate_canvas(= /api/canvas/preview)를 이미 통과한 것이라 DAG 로 변환은 되지만,
 * 화면에서 쓰려면 몇 가지가 더 필요하다 — 알 수 없는 노드 유형 제거, 라벨·좌표 보정,
 * 그리고 "추가로 붙이기" 를 할 때의 id 충돌 회피.
 */
import type { Node, Edge } from '@xyflow/react';
import { INSERTABLE_NODE_TYPES, typeLabelOf } from '../nodeCatalog';
import i18n from '../../../i18n';

export interface Fragment { nodes: Node[]; edges: Edge[] }

/** 캔버스가 아는 노드 유형 — 카탈로그 + 묶음/주석 노드 */
const KNOWN = new Set<string>([
  ...INSERTABLE_NODE_TYPES.map((e) => e.type),
  'group', 'subflow', 'repeatGroup', 'memo',
]);


const GRID_X = 260;
const GRID_Y = 130;

/**
 * 제안 산출물 → 캔버스 조각. 넣을 수 없는 것은 버리고 이유를 함께 돌려준다
 * (조용히 사라지면 사용자가 "왜 노드가 비었지" 하게 된다).
 */
export function normalizeProposal(artifact: any): { fragment: Fragment; dropped: string[] } {
  let src: any = artifact;
  if (typeof src === 'string') {
    try { src = JSON.parse(src); } catch { return { fragment: { nodes: [], edges: [] }, dropped: [i18n.t('workflow:proposal.badJson')] }; }
  }
  const rawNodes: any[] = Array.isArray(src?.nodes) ? src.nodes : [];
  const rawEdges: any[] = Array.isArray(src?.edges) ? src.edges : [];
  const dropped: string[] = [];

  const nodes: Node[] = [];
  const seen = new Set<string>();
  rawNodes.forEach((n: any, i: number) => {
    const id = String(n?.id || '').trim();
    const type = String(n?.type || '').trim();
    if (!id || seen.has(id)) { dropped.push(i18n.t('workflow:proposal.badId', { where: id || i + 1 })); return; }
    if (!KNOWN.has(type)) { dropped.push(i18n.t('workflow:proposal.unknownType', { type: type || i18n.t('workflow:proposal.none'), id })); return; }
    seen.add(id);
    const data = (n.data && typeof n.data === 'object') ? { ...n.data } : {};
    if (!data.label) data.label = typeLabelOf(type);
    if (!data.config || typeof data.config !== 'object') data.config = {};
    const pos = n.position && typeof n.position === 'object' ? n.position : null;
    nodes.push({
      id,
      type,
      // 좌표를 안 줬으면 순서대로 가로로 놓는다 — 겹쳐서 한 점에 쌓이는 것보다 낫다
      position: { x: Number(pos?.x ?? (i % 5) * GRID_X), y: Number(pos?.y ?? Math.floor(i / 5) * GRID_Y) },
      data,
      ...(n.parentId && seen.has(String(n.parentId)) ? { parentId: String(n.parentId) } : {}),
    } as Node);
  });

  const edges: Edge[] = [];
  const nodeIds = new Set(nodes.map((n) => n.id));
  rawEdges.forEach((e: any, i: number) => {
    const source = String(e?.source || '');
    const target = String(e?.target || '');
    if (!nodeIds.has(source) || !nodeIds.has(target)) {
      dropped.push(i18n.t('workflow:proposal.danglingEdge', { source: source || '?', target: target || '?' }));
      return;
    }
    edges.push({
      id: String(e?.id || `e_${source}_${target}_${i}`),
      source,
      target,
      ...(e.sourceHandle ? { sourceHandle: String(e.sourceHandle) } : {}),
      ...(e.targetHandle ? { targetHandle: String(e.targetHandle) } : {}),
      ...(e.label ? { label: e.label } : {}),
    } as Edge);
  });

  return { fragment: { nodes, edges }, dropped };
}

/**
 * 지금 캔버스와 id 가 겹치지 않게 새로 매긴다 — "추가로 붙이기" 용.
 * 겹치는 것만 바꾸면 어떤 것은 그대로고 어떤 것은 아니라 헷갈리므로 전부 새로 매긴다.
 */
export function withFreshIds(frag: Fragment, offset = { x: 40, y: 40 }): Fragment {
  const stamp = Date.now().toString(36);
  const map: Record<string, string> = {};
  frag.nodes.forEach((n, i) => { map[n.id] = `${n.id}_a${stamp}${i.toString(36)}`; });
  const nodes = frag.nodes.map((n: any) => ({
    ...n,
    id: map[n.id],
    selected: true,
    position: n.parentId ? { ...n.position } : { x: n.position.x + offset.x, y: n.position.y + offset.y },
    ...(n.parentId && map[n.parentId] ? { parentId: map[n.parentId] } : {}),
  })) as Node[];
  const edges = frag.edges.map((e: any, i) => ({
    ...e,
    id: `${e.id}_a${stamp}${i.toString(36)}`,
    source: map[e.source],
    target: map[e.target],
  })) as Edge[];
  return { nodes, edges };
}
